import React, { createContext, useContext, useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import DashboardPage from "./pages/DashboardPage";
import TransformerPage from "./pages/TransformerPage";
import SwitchgearPage from "./pages/SwitchgearPage";
import MotorPage from "./pages/MotorPage";
import CablePage from "./pages/CablePage";
import SummaryPage from "./pages/SummaryPage";
import ScenarioPresentationPage from "./pages/ScenarioPresentationPage";
import OrganizationPage from "./pages/OrganizationPage";
import SessionTrackerPage from "./pages/SessionTrackerPage";
import AdminDashboard from "./pages/AdminDashboard";
import AssetConfigPage from "./pages/AssetConfigPage";
import CustomersPage from "./pages/CustomersPage";
import TeamPage from "./pages/TeamPage";
import SessionsPage from "./pages/SessionsPage";

// Components
import Layout, { PresentationProvider } from "./components/Layout";
import NotesPanel from "./components/NotesPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Meeting Context
const MeetingContext = createContext(null);

export const useMeeting = () => useContext(MeetingContext);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  // Refresh user data (useful after joining/leaving organization)
  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        return response.data;
      } catch (error) {
        console.error("Failed to refresh user:", error);
      }
    }
    return null;
  };

  useEffect(() => {
    const validateToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    validateToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, organizationName = null) => {
    const payload = { name, email, password };
    if (organizationName) {
      payload.organization_name = organizationName;
    }
    const response = await axios.post(`${API}/auth/register`, payload);
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Meeting Provider
const MeetingProvider = ({ children }) => {
  const { token } = useAuth();
  const [currentMeeting, setCurrentMeeting] = useState(null);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [discussionPoints, setDiscussionPoints] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [actionItems, setActionItems] = useState([]);

  const createMeeting = async (customerName, company, primaryAssetFocus, customerId = null) => {
    const response = await axios.post(
      `${API}/meetings`,
      { 
        customer_name: customerName, 
        company, 
        primary_asset_focus: primaryAssetFocus,
        customer_id: customerId
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCurrentMeeting(response.data);
    setSelectedAssets(response.data.selected_assets || []);
    setDiscussionPoints(response.data.discussion_points || []);
    setQuestions(response.data.questions || []);
    setActionItems(response.data.action_items || []);
    return response.data;
  };

  const loadMeeting = async (meetingId) => {
    const response = await axios.get(`${API}/meetings/${meetingId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setCurrentMeeting(response.data);
    setSelectedAssets(response.data.selected_assets || []);
    setDiscussionPoints(response.data.discussion_points || []);
    setQuestions(response.data.questions || []);
    setActionItems(response.data.action_items || []);
    return response.data;
  };

  const updateNotes = async (notes) => {
    if (!currentMeeting) return;
    const response = await axios.patch(
      `${API}/meetings/${currentMeeting.id}`,
      { notes },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCurrentMeeting(response.data);
  };

  const updateSelectedAssets = async (assets) => {
    if (!currentMeeting) return;
    setSelectedAssets(assets);
    const response = await axios.patch(
      `${API}/meetings/${currentMeeting.id}`,
      { selected_assets: assets },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCurrentMeeting(response.data);
  };

  // Auto-add discussion point when viewing an item
  const addDiscussionPoint = async (text, itemType = "manual", itemId = null, itemName = null, assetType = null) => {
    if (!currentMeeting) return;
    
    const newPoint = {
      id: `dp-${Date.now()}`,
      text,
      timestamp: new Date().toISOString(),
      status: "discussed",
      item_type: itemType,
      item_id: itemId,
      item_name: itemName,
      asset_type: assetType
    };
    
    const updatedPoints = [...discussionPoints, newPoint];
    setDiscussionPoints(updatedPoints);
    
    await axios.patch(
      `${API}/meetings/${currentMeeting.id}`,
      { discussion_points: updatedPoints },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const updateDiscussionPointStatus = async (pointId, status) => {
    if (!currentMeeting) return;
    
    const updatedPoints = discussionPoints.map(p => 
      p.id === pointId ? { ...p, status } : p
    );
    setDiscussionPoints(updatedPoints);
    
    await axios.patch(
      `${API}/meetings/${currentMeeting.id}`,
      { discussion_points: updatedPoints },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  // Questions management
  const addQuestion = async (questionText, priority = "medium") => {
    if (!currentMeeting) return;
    
    const newQuestion = {
      id: `q-${Date.now()}`,
      question: questionText,
      answer: "",
      status: "open",
      priority,
      created_at: new Date().toISOString(),
      answered_at: null
    };
    
    const updatedQuestions = [...questions, newQuestion];
    setQuestions(updatedQuestions);
    
    await axios.patch(
      `${API}/meetings/${currentMeeting.id}`,
      { questions: updatedQuestions },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const updateQuestion = async (questionId, updates) => {
    if (!currentMeeting) return;
    
    const updatedQuestions = questions.map(q => {
      if (q.id === questionId) {
        const updated = { ...q, ...updates };
        if (updates.status === "answered" && !q.answered_at) {
          updated.answered_at = new Date().toISOString();
        }
        return updated;
      }
      return q;
    });
    setQuestions(updatedQuestions);
    
    await axios.patch(
      `${API}/meetings/${currentMeeting.id}`,
      { questions: updatedQuestions },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const deleteQuestion = async (questionId) => {
    if (!currentMeeting) return;
    
    const updatedQuestions = questions.filter(q => q.id !== questionId);
    setQuestions(updatedQuestions);
    
    await axios.patch(
      `${API}/meetings/${currentMeeting.id}`,
      { questions: updatedQuestions },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  // Action items management
  const addActionItem = async (description, assignedTo = "", dueDate = null) => {
    if (!currentMeeting) return;
    
    const newAction = {
      id: `a-${Date.now()}`,
      description,
      assigned_to: assignedTo,
      due_date: dueDate,
      status: "pending",
      created_at: new Date().toISOString(),
      completed_at: null
    };
    
    const updatedActions = [...actionItems, newAction];
    setActionItems(updatedActions);
    
    await axios.patch(
      `${API}/meetings/${currentMeeting.id}`,
      { action_items: updatedActions },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const updateActionItem = async (actionId, updates) => {
    if (!currentMeeting) return;
    
    const updatedActions = actionItems.map(a => {
      if (a.id === actionId) {
        const updated = { ...a, ...updates };
        if (updates.status === "completed" && !a.completed_at) {
          updated.completed_at = new Date().toISOString();
        }
        return updated;
      }
      return a;
    });
    setActionItems(updatedActions);
    
    await axios.patch(
      `${API}/meetings/${currentMeeting.id}`,
      { action_items: updatedActions },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const deleteActionItem = async (actionId) => {
    if (!currentMeeting) return;
    
    const updatedActions = actionItems.filter(a => a.id !== actionId);
    setActionItems(updatedActions);
    
    await axios.patch(
      `${API}/meetings/${currentMeeting.id}`,
      { action_items: updatedActions },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const toggleAsset = (type, id, assetCategory, itemName = null) => {
    const exists = selectedAssets.find(
      (a) => a.type === type && a.id === id && a.asset === assetCategory
    );
    
    let newAssets;
    if (exists) {
      newAssets = selectedAssets.filter(
        (a) => !(a.type === type && a.id === id && a.asset === assetCategory)
      );
    } else {
      newAssets = [...selectedAssets, { type, id, asset: assetCategory }];
      // Auto-add discussion point when selecting an item
      if (itemName) {
        const typeLabel = type === "failure_mode" ? "Failure Mode" : type === "test" ? "Test" : "Monitoring";
        addDiscussionPoint(
          `Discussed ${typeLabel}: ${itemName}`,
          type,
          id,
          itemName,
          assetCategory
        );
      }
    }
    
    updateSelectedAssets(newAssets);
  };

  const isAssetSelected = (type, id, assetCategory) => {
    return selectedAssets.some(
      (a) => a.type === type && a.id === id && a.asset === assetCategory
    );
  };

  const clearMeeting = () => {
    setCurrentMeeting(null);
    setSelectedAssets([]);
    setDiscussionPoints([]);
    setQuestions([]);
    setActionItems([]);
  };

  return (
    <MeetingContext.Provider
      value={{
        currentMeeting,
        selectedAssets,
        discussionPoints,
        questions,
        actionItems,
        createMeeting,
        loadMeeting,
        updateNotes,
        updateSelectedAssets,
        toggleAsset,
        isAssetSelected,
        clearMeeting,
        addDiscussionPoint,
        updateDiscussionPointStatus,
        addQuestion,
        updateQuestion,
        deleteQuestion,
        addActionItem,
        updateActionItem,
        deleteActionItem
      }}
    >
      {children}
    </MeetingContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Routes
const AppRoutes = () => {
  const { user } = useAuth();
  const { currentMeeting } = useMeeting();

  // Determine where to redirect logged-in users
  const getDefaultRedirect = () => {
    if (user?.is_master_admin) {
      return "/admin";
    }
    return "/sessions";
  };

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={getDefaultRedirect()} replace /> : <LoginPage />} />
      <Route
        path="/landing"
        element={
          <ProtectedRoute>
            {user?.is_master_admin ? <Navigate to="/admin" replace /> : <LandingPage />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {user?.is_master_admin ? <Navigate to="/admin" replace /> : (
              <Layout>
                <DashboardPage />
              </Layout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/transformers"
        element={
          <ProtectedRoute>
            <Layout>
              <TransformerPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/switchgear"
        element={
          <ProtectedRoute>
            <Layout>
              <SwitchgearPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/motors"
        element={
          <ProtectedRoute>
            <Layout>
              <MotorPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/cables"
        element={
          <ProtectedRoute>
            <Layout>
              <CablePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/summary"
        element={
          <ProtectedRoute>
            <Layout>
              <SummaryPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scenario/:scenarioId"
        element={
          <ProtectedRoute>
            <ScenarioPresentationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/organization"
        element={
          <ProtectedRoute>
            <Layout>
              <OrganizationPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Layout>
              <AdminDashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/assets"
        element={
          <ProtectedRoute>
            <Layout>
              <AssetConfigPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <Layout>
              <CustomersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <Layout>
              <TeamPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sessions"
        element={
          <ProtectedRoute>
            <Layout>
              <SessionsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/session-tracker"
        element={
          <ProtectedRoute>
            <SessionTrackerPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={user ? <Navigate to={getDefaultRedirect()} replace /> : <Navigate to="/login" replace />} />
      <Route path="*" element={user ? <Navigate to={getDefaultRedirect()} replace /> : <Navigate to="/login" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <MeetingProvider>
          <PresentationProvider>
            <div className="app-container">
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </div>
          </PresentationProvider>
        </MeetingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
